import { settings } from '../../app/settings';

export type Sfx =
  | 'click'
  | 'hover'
  | 'jump'
  | 'land'
  | 'step'
  | 'push'
  | 'whoosh'
  | 'slip'
  | 'paint'
  | 'inflate'
  | 'teleport'
  | 'pickup'
  | 'drop'
  | 'deliver'
  | 'valve'
  | 'plate'
  | 'stage'
  | 'victory'
  | 'defeat'
  | 'alarm'
  | 'button'
  | 'coin'
  | 'beep'
  | 'go'
  | 'chat'
  | 'error'
  | 'banana'
  | 'blocked'
  | 'pad';

/**
 * Audio 100 % sintetizado con WebAudio (efectos y música originales, sin ficheros externos).
 * No se crea el AudioContext hasta la primera interacción del usuario (política de autoplay).
 */
class AudioSystem {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private musicGain!: GainNode;
  private sfxGain!: GainNode;
  private noise!: AudioBuffer;
  private musicTimer: number | null = null;
  private musicStep = 0;
  private nextNoteTime = 0;
  intensity: 'menu' | 'calm' | 'mission' = 'menu';

  /** Llamar desde un gesto del usuario. */
  unlock() {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.musicGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();
    this.musicGain.connect(this.master);
    this.sfxGain.connect(this.master);
    this.master.connect(this.ctx.destination);
    this.noise = this.ctx.createBuffer(1, this.ctx.sampleRate, this.ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    this.applyVolumes();
    settings.subscribe(() => this.applyVolumes());
    this.startMusic();
  }

  get ready() {
    return !!this.ctx && this.ctx.state === 'running';
  }

  private applyVolumes() {
    if (!this.ctx) return;
    const s = settings.get();
    this.master.gain.value = s.master;
    this.musicGain.gain.value = s.music * 0.35;
    this.sfxGain.gain.value = s.sfx;
  }

  private tone(freq: number, dur: number, type: OscillatorType = 'square', vol = 0.2, slideTo?: number, delay = 0, dest?: AudioNode) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime + delay;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(dest ?? this.sfxGain);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private hiss(dur: number, vol = 0.2, filterFreq = 1200, delay = 0, type: BiquadFilterType = 'bandpass', dest?: AudioNode) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime + delay;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    const f = this.ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = filterFreq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g).connect(dest ?? this.sfxGain);
    src.start(t, Math.random() * 0.5);
    src.stop(t + dur + 0.02);
  }

  play(s: Sfx, vol = 1) {
    if (!this.ctx) return;
    const v = vol;
    switch (s) {
      case 'click':
        this.tone(660, 0.06, 'square', 0.08 * v);
        break;
      case 'hover':
        this.tone(880, 0.03, 'sine', 0.04 * v);
        break;
      case 'jump':
        this.tone(300, 0.18, 'square', 0.1 * v, 720);
        break;
      case 'land':
        this.hiss(0.12, 0.25 * v, 300, 0, 'lowpass');
        break;
      case 'step':
        this.hiss(0.04, 0.06 * v, 1800 + Math.random() * 600);
        break;
      case 'push':
        this.hiss(0.2, 0.25 * v, 900);
        this.tone(180, 0.35, 'sine', 0.25 * v, 520, 0.05);
        break;
      case 'whoosh':
        this.hiss(0.18, 0.12 * v, 1400);
        break;
      case 'slip':
        this.tone(1400, 0.6, 'sine', 0.18 * v, 200);
        break;
      case 'paint':
        this.hiss(0.3, 0.35 * v, 500, 0, 'lowpass');
        this.tone(140, 0.2, 'sine', 0.2 * v, 60);
        break;
      case 'inflate':
        this.tone(200, 0.7, 'sawtooth', 0.08 * v, 900);
        this.tone(900, 0.12, 'sine', 0.15 * v, 1200, 0.7);
        break;
      case 'teleport':
        [523, 784, 1047, 1568].forEach((f, i) => this.tone(f, 0.12, 'triangle', 0.12 * v, undefined, i * 0.05));
        break;
      case 'pickup':
        this.tone(660, 0.1, 'square', 0.1 * v);
        this.tone(990, 0.14, 'square', 0.1 * v, undefined, 0.08);
        break;
      case 'drop':
        this.tone(400, 0.15, 'triangle', 0.1 * v, 200);
        break;
      case 'deliver':
        [523, 659, 784, 1047].forEach((f) => this.tone(f, 0.5, 'triangle', 0.08 * v));
        this.tone(1568, 0.3, 'sine', 0.08 * v, undefined, 0.15);
        break;
      case 'valve':
        this.tone(220, 0.08, 'square', 0.1 * v);
        this.hiss(0.1, 0.08 * v, 3000, 0.04);
        break;
      case 'plate':
        this.tone(330, 0.1, 'sine', 0.12 * v, 440);
        break;
      case 'stage':
        [523, 659, 784].forEach((f, i) => this.tone(f, 0.18, 'square', 0.09 * v, undefined, i * 0.1));
        this.tone(1047, 0.45, 'square', 0.09 * v, undefined, 0.3);
        break;
      case 'victory':
        [392, 523, 659, 784, 659, 784, 1047].forEach((f, i) => this.tone(f, i === 6 ? 0.8 : 0.16, 'square', 0.09 * v, undefined, i * 0.13));
        break;
      case 'defeat':
        [392, 370, 349, 262].forEach((f, i) => this.tone(f, i === 3 ? 0.9 : 0.35, 'sawtooth', 0.08 * v, i === 3 ? 180 : undefined, i * 0.35));
        break;
      case 'alarm':
        for (let i = 0; i < 4; i++) this.tone(700, 0.5, 'sawtooth', 0.07 * v, 1100, i * 0.5);
        break;
      case 'button':
        this.tone(120, 0.15, 'square', 0.15 * v);
        this.tone(500, 0.4, 'sine', 0.12 * v, 150, 0.1);
        break;
      case 'coin':
        this.tone(988, 0.07, 'square', 0.08 * v);
        this.tone(1319, 0.2, 'square', 0.08 * v, undefined, 0.07);
        break;
      case 'beep':
        this.tone(660, 0.15, 'square', 0.1 * v);
        break;
      case 'go':
        this.tone(990, 0.4, 'square', 0.12 * v);
        break;
      case 'chat':
        this.tone(1200, 0.06, 'sine', 0.06 * v);
        break;
      case 'error':
        this.tone(160, 0.25, 'square', 0.1 * v);
        break;
      case 'banana':
        this.hiss(0.25, 0.15 * v, 2500);
        this.tone(500, 0.2, 'sine', 0.1 * v, 900);
        break;
      case 'blocked':
        this.tone(300, 0.08, 'triangle', 0.1 * v);
        this.tone(250, 0.12, 'triangle', 0.1 * v, undefined, 0.09);
        break;
      case 'pad':
        this.tone(200, 0.4, 'sine', 0.2 * v, 1200);
        break;
    }
  }

  // --------------------------------------------------------------- música
  private startMusic() {
    if (!this.ctx || this.musicTimer !== null) return;
    this.nextNoteTime = this.ctx.currentTime + 0.1;
    this.musicTimer = window.setInterval(() => this.schedule(), 50);
  }

  private schedule() {
    if (!this.ctx) return;
    const bpm = this.intensity === 'mission' ? 132 : 112;
    const stepDur = 60 / bpm / 4;
    // Progresión alegre: C – Am – F – G
    const chords = [
      [261.6, 329.6, 392.0],
      [220.0, 261.6, 329.6],
      [174.6, 220.0, 261.6],
      [196.0, 246.9, 293.7],
    ];
    const melody = [0, 2, 4, 2, 5, 4, 2, 0, 4, 5, 7, 5, 4, 2, 4, -1];
    const scale = [523.3, 587.3, 659.3, 698.5, 784.0, 880.0, 987.8, 1046.5];
    while (this.nextNoteTime < this.ctx.currentTime + 0.2) {
      const step = this.musicStep % 64;
      const bar = Math.floor(step / 16);
      const t = this.nextNoteTime - this.ctx.currentTime;
      const chord = chords[bar];
      if (step % 4 === 0) this.tone(chord[0] / 2, stepDur * 3, 'triangle', 0.32, undefined, t, this.musicGain);
      if (step % 8 === 4) chord.forEach((f) => this.tone(f, stepDur * 2, 'square', 0.035, undefined, t, this.musicGain));
      if (this.intensity !== 'menu' || step % 2 === 0) this.hiss(0.03, step % 4 === 2 ? 0.08 : 0.04, 7000, t, 'highpass', this.musicGain);
      if (step % 8 === 0) this.hiss(0.08, 0.18, 120, t, 'lowpass', this.musicGain);
      const n = melody[step % 16];
      if (step % 2 === 0 && n >= 0 && (this.intensity !== 'calm' || bar % 2 === 0))
        this.tone(scale[n % scale.length] * (bar === 3 ? 0.89 : 1), stepDur * 1.6, 'square', 0.04, undefined, t, this.musicGain);
      this.nextNoteTime += stepDur;
      this.musicStep++;
    }
  }
}

export const audio = new AudioSystem();
